import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { workdStorage } from '../storage/workdStorage';

/**
 * RingCentral call preferences
 */
export interface RingCentralPreferences {
	callMethodPreference: string | null;
	deviceId: string | null;
	deviceName: string | null;
	forwardingNumberId: string | null;
	dialMethod: string | null;
	ringOutPhoneNumber: string | null;
}

/**
 * Vonage call preferences
 */
export interface VonagePreferences {
	callMethodPreference: 'vonage' | 'phone_app' | null;
}

/**
 * User preferences state
 */
export interface PreferencesStoreState {
	language: string | null;
	theme: 'light' | 'dark' | null;
	ringCentral: RingCentralPreferences;
	vonage: VonagePreferences;

	// Config-web preferences
	configSidebarCollapsed: boolean;
	configPublicUserId: string | null;
}

/**
 * User preferences actions
 */
export interface PreferencesStoreActions {
	setLanguage: (language: string | null) => void;
	setTheme: (theme: 'light' | 'dark' | null) => void;
	setRingCentralPreference: <K extends keyof RingCentralPreferences>(
		key: K,
		value: RingCentralPreferences[K],
	) => void;
	setRingCentralPreferences: (prefs: Partial<RingCentralPreferences>) => void;
	setVonagePreference: <K extends keyof VonagePreferences>(
		key: K,
		value: VonagePreferences[K],
	) => void;
	setVonagePreferences: (prefs: Partial<VonagePreferences>) => void;
	setConfigSidebarCollapsed: (collapsed: boolean) => void;
	setConfigPublicUserId: (userId: string | null) => void;
	clearAll: () => void;
}

const initialRingCentralState: RingCentralPreferences = {
	callMethodPreference: null,
	deviceId: null,
	deviceName: null,
	forwardingNumberId: null,
	dialMethod: null,
	ringOutPhoneNumber: null,
};

const initialVonageState: VonagePreferences = {
	callMethodPreference: null,
};

const initialState: PreferencesStoreState = {
	language: null,
	theme: null,
	ringCentral: initialRingCentralState,
	vonage: initialVonageState,
	configSidebarCollapsed: false,
	configPublicUserId: null,
};

/**
 * Zustand store for user preferences.
 * Persists to localStorage with workd:preferences:state key.
 *
 * Stores:
 * - Language preference
 * - Theme preference (light/dark)
 * - RingCentral call settings
 * - Vonage call settings
 * - Config-web sidebar and user settings
 */
export const usePreferencesStore = create<
	PreferencesStoreState & PreferencesStoreActions
>()(
	persist(
		(set) => ({
			...initialState,

			setLanguage: (language) => {
				set({ language });
			},

			setTheme: (theme) => {
				set({ theme });
			},

			setRingCentralPreference: (key, value) => {
				set((state) => ({
					ringCentral: {
						...state.ringCentral,
						[key]: value,
					},
				}));
			},

			setRingCentralPreferences: (prefs) => {
				set((state) => ({
					ringCentral: {
						...state.ringCentral,
						...prefs,
					},
				}));
			},

			setVonagePreference: (key, value) => {
				set((state) => ({
					vonage: {
						...state.vonage,
						[key]: value,
					},
				}));
			},

			setVonagePreferences: (prefs) => {
				set((state) => ({
					vonage: {
						...state.vonage,
						...prefs,
					},
				}));
			},

			setConfigSidebarCollapsed: (configSidebarCollapsed) => {
				set({ configSidebarCollapsed });
			},

			setConfigPublicUserId: (configPublicUserId) => {
				set({ configPublicUserId });
			},

			clearAll: () => {
				set(initialState);
			},
		}),
		{
			name: 'workd:preferences:state',
			storage: workdStorage,
		},
	),
);
